// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { DynProperty } from "../Commons/DynProperty.js";
import { Instrument } from "../Commons/cache/Instrument.js";
import { Resources } from "../Commons/properties/Resources.js";
import { DockSystemInstance } from "./DockSystem.js";
import { Factory, FactoryAddPanelFrom, PanelDisplayMode, ThrottlingOperation } from "./panels/Factory.js";


export let WorkSpace = function (name, fileName)
{
    this.visibleName = name;
    this.fileName = fileName;
    this.allPanels = {};
    this.locked = Resources.isHidden('allowWorkspaceEdit');
    this.isDirty = false;
};

WorkSpace.prototype.getPanelsAmount = function ()
{
    return Object.keys(this.allPanels).length;
};

// TODO. Exclusively for Brazil: hidden
WorkSpace.prototype.updateLocked = function (locked)
{
    if (Resources.isHidden('allowWorkspaceEdit'))
        this.locked = true;
    else if (locked !== undefined)
        this.locked = !!locked;
};

WorkSpace.prototype.serializePanels = function ()
{
    Instrument.saveMode = true;
    let serializedPanelArray = new Array();
    let allPanels = this.allPanels;
    let insNamesDict = new Object();
    let i = 0;
    for (let key in allPanels)
    {
        let panel = allPanels[key];
        if (panel.skipOnSave)
            continue;
        panel.setWorkSpaceId(i++);

        let serializedPanel = {};
        serializedPanel.name = panel.getType();
        serializedPanel.properties = DynProperty.serialize(panel.Properties());

        let xmlSettingsTemplate = panel.getXmlSettingsTemplate();
        if (xmlSettingsTemplate)
            serializedPanel.xmlSettingsTemplate = xmlSettingsTemplate;

        serializedPanelArray.push(serializedPanel);

        let ins = panel.getInstrument();
        if (ins)
            insNamesDict[ins.GetInteriorID()] = 1;
    }

    Instrument.saveMode = false;
    return {
        panels: serializedPanelArray,
        dockSystemState: DockSystemInstance.getState(),
        insDict: insNamesDict
    };

};

WorkSpace.prototype.loadPanels = function (serializedPanelsData)
{
    if (!serializedPanelsData ||
        !serializedPanelsData.dockSystemState ||
        !serializedPanelsData.panels)
        return;

    var dockSystemState = serializedPanelsData.dockSystemState ? JSON.parse(serializedPanelsData.dockSystemState) : null
    let serializedPanelArray = serializedPanelsData.panels;
    for (let i = 0; i < serializedPanelArray.length; i++)
    {
        let serializedPanel = serializedPanelArray[i]
        let emptyPanel = Factory.createPanel(serializedPanel.name);
        let properties = DynProperty.deserialize(serializedPanel.properties);

        let validProperties = emptyPanel.validateProperties(properties);
        if (validProperties)
        {
            let newPanel = Factory.addPanel(serializedPanel.name, FactoryAddPanelFrom.Restore);
            newPanel.callBack(properties);
            newPanel.setXmlSettingsTemplate(serializedPanel.xmlSettingsTemplate);

            DockSystemInstance.addToArrRestore(newPanel);
        }
        else
            DockSystemInstance.deletePanelFromDockSystemState(emptyPanel.GetWSIdFromProperty(properties))
    };

    DockSystemInstance.setState(JSON.stringify(dockSystemState));
};

WorkSpace.prototype.clear = function ()
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
        allPanels[panelName].dispose();

    this.allPanels = {};

    DockSystemInstance.clearDocking();
};

// TODO.
WorkSpace.prototype.dispose = function ()
{
    this.clear();
};

WorkSpace.prototype.addPanel = function (panel)
{
    let panelType = panel.getType();
    let isAllowedByDisplayMode = Factory.GetDisplayMode(panelType, null) == PanelDisplayMode.Allowed; // rule check
    let isAllowedByLimit = Factory.GetThrottlingOperationValue(panelType).throttlingOperation == ThrottlingOperation.CanAddPanel; // panels limit number check

    this.allPanels[panel._guid] = panel;

    if (isAllowedByDisplayMode && isAllowedByLimit)
        panel.populate(true);
    else
        panel.set('forbiddenPanel', true);

    this.isDirty = true;
};

// TODO. Confusing name.
WorkSpace.prototype.removePanel = function (id)
{
    delete this.allPanels[id];
};

WorkSpace.prototype.createPropertiesHash = function (propArray)
{
    let result = {};
    for (let i = 0, len = propArray.length; i < len; i++)
    {
        let prop = propArray[i];
        result[prop.name] = prop;
    }
    return result;
};

WorkSpace.prototype.getElementByWSID = function (id)
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
    {
        let panel = allPanels[panelName];
        if (panel.get('workSpaceId') === id)
            return panel;
    }
    return null;
};

WorkSpace.prototype.populate = function ()
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
    {
        allPanels[panelName].populate();
        this.isDirty = true;
    }
};

WorkSpace.prototype.TickAsync = function ()
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
        allPanels[panelName].TickAsync();
};

WorkSpace.prototype.updateSettings = function ()
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
        allPanels[panelName].updateSettings();
};

WorkSpace.prototype.localize = function ()
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
        allPanels[panelName].localize();
};

WorkSpace.prototype.RefreshPanelsWithInstrumentDescriptions = function (insArray)
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
        allPanels[panelName].RefreshInstrumentDescriptions(insArray);
};

WorkSpace.prototype.themeChange = function ()
{
    let allPanels = this.allPanels;
    for (let panelName in allPanels)
        allPanels[panelName].themeChange();
};

WorkSpace.prototype.numberOfPanelByType = function (panelType) 
{
    let allPanels = this.allPanels, result = 0
    for (let panelName in allPanels)
        if (allPanels[panelName].getType() == panelType)
            result++
    return result
};